/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
  Input,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import NumericInput from 'components/NumericInput';
import CurrencyInput from 'components/CurrencyInput';
import PercentageInput from 'components/PercentageInput';
import currencyFormatter from 'utils/currencyFormatter';
import percentFormatter from 'utils/percentFormatter';
import ModalDelete from 'components/ModalDelete';
import { FLOAT_NUMER_PATTERN, integerRegExp } from 'utils/patterns';
import Select from 'components/Select';
import {
  getPercepcionesConfiguracionesAntiguedades,
  postPercepcionConfiguracionAntiguedad,
  deletePercepcionConfiguracionAntiguedad,
  patchPercepcionConfiguracionAntiguedad,
  getPercepcionConfiguracionAntiguedad,
} from 'api/nominas/percepciones/percepcionesConfiguracionesAntiguedades';
import { checkToken } from 'api/utils';

export const percepcionConfiguracionAntiguedadPermission = {
  permissionModel: 'percepcionconfiguracionantiguedad',
  catalogsModels: [
    'percepcion',
  ],
  tabsModels: [],
};

const PercepcionConfiguracionAntiguedad = ({
  permission,
  selected,
  antiguedadLaboral = true,
  estatusAutorizadoInactivo,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [aplicaPorcentaje, setAplicaPorcentaje] = useState(true);

  const formInstances = [
    form,
  ];

  const filterData = (antiguedades) => {
    let newData;

    if (antiguedadLaboral) {
      newData = antiguedades.filter((value) => value.por_antiguedad_porcentaje === true);
    } else {
      newData = antiguedades.filter((value) => value.por_antiguedad_porcentaje === false);
    }

    return newData;
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPercepcionesConfiguracionesAntiguedades({
      percepciones: selected,
      por_antiguedad_porcentaje: antiguedadLaboral,
    });
    const newData = filterData(_data);
    setData(formatReceived(newData));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
      ] = await Promise.all([
        getPercepcionesConfiguracionesAntiguedades({
          percepciones: selected,
          por_antiguedad_porcentaje: antiguedadLaboral,
        }),
      ]);
      const newData = filterData(_data);
      if (mounted) {
        setData(formatReceived(newData));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    setAplicaPorcentaje(true);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _configuracion = await getPercepcionConfiguracionAntiguedad(match.id);
    setTimeout(() => {
      if (_configuracion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_configuracion));
          form.setFieldValue('aplica_importe_fijo', true);
          setVisible(true);
          setAplicaPorcentaje(!form.getFieldValue('aplica_porcentaje'));
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key ? patchPercepcionConfiguracionAntiguedad(key, { ...values, percepciones: selected, por_antiguedad_porcentaje: antiguedadLaboral })
        : postPercepcionConfiguracionAntiguedad({ ...values, percepciones: selected, por_antiguedad_porcentaje: antiguedadLaboral }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionConfiguracionAntiguedad(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onAnioChange = async () => {
    const { de_anos, hasta_anos } = form.getFieldsValue();
    if (de_anos && hasta_anos) {
      await form.validateFields(['de_anos', 'hasta_anos']);
    }
  };

  const onDiaChange = async () => {
    const { de_dias, hasta_dias } = form.getFieldsValue();
    if (de_dias && hasta_dias) {
      await form.validateFields(['de_dias', 'hasta_dias']);
    }
  };

  const genericColumns = [
    {
      titleText: 'De Número de Años',
      dataIndex: 'de_anos_texto',
      key: 'de_anos_texto',
      width: 200,
    },
    {
      titleText: 'De Número de Días',
      dataIndex: 'de_dias',
      key: 'de_dias',
      width: 200,
    },
    {
      titleText: 'Hasta Número de Años',
      dataIndex: 'hasta_anos_texto',
      key: 'hasta_anos_texto',
      width: 200,
    },
    {
      titleText: 'Hasta Número de Días',
      dataIndex: 'hasta_dias',
      key: 'hasta_dias',
      width: 200,
    },
  ];

  const columFormat1 = [
    ...genericColumns,
    {
      titleText: 'Importe Fijo',
      dataIndex: 'importe_fijo',
      key: 'importe_fijo',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigente_al',
      key: 'vigente_al',
      width: 200,
      render: (val) => val?.format(),
    },
  ];

  const columFormat2 = [
    ...genericColumns,
    {
      titleText: 'Número de Días',
      dataIndex: 'dias',
      key: 'dias',
      width: 200,
    },
    {
      titleText: '%',
      dataIndex: 'porcentaje_de_percepcion',
      key: 'porcentaje_de_percepcion',
      width: 200,
      align: 'right',
      render: (val) => percentFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigente_al',
      key: 'vigente_al',
      width: 200,
      render: (val) => val?.format(),
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    integer: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
    ],
    float: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    menor_anos: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async () => {
          const { de_anos, hasta_anos } = form.getFieldsValue();
          const de_anosNum = parseInt(de_anos, 10);
          const hasta_anosNum = parseInt(hasta_anos, 10);
          if (de_anos > 100 || hasta_anos > 100) {
            throw new Error('El rango no puede ser mayor a 100');
          } else if (de_anosNum > hasta_anosNum) {
            throw new Error('El valor ingresado debe ser mayor al del campo "De Número de Años"');
          }
        },
      },
    ],
    porcentaje_de_percepcion: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async (_, value) => {
          if (value > 100) {
            throw new Error('Este campo no puede ser mayor a 100%');
          }
        },
      },
    ],
  };

  const renderColumnFormat1 = () => (
    <Row gutter={10} style={{ width: '100%', marginLeft: 0, marginRight: 0 }}>
      <Form.Item
        name="aplica_importe_fijo"
        label="Aplica Importe Fijo"
        rules={rules.required}
        hidden
      >
        <Select
          trueFalse
          disabled
        />
      </Form.Item>
      <Col xs={24} sm={24} md={12}>
        <CurrencyInput
          name="importe_fijo"
          label="Importe Fijo"
          rules={rules.float}
          precision={2}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Form.Item
          name="vigente_al"
          label="Vigencia Final"
          hasFeedback
        >
          <DatePicker
            allowClear
            format="DD/MM/YYYY"
          />
        </Form.Item>
      </Col>
    </Row>
  );

  const renderColumnFormat2 = () => (
    <Row gutter={10} style={{ width: '100%', marginLeft: 0, marginRight: 0 }}>
      <Col xs={24} sm={24} md={12}>
        <NumericInput
          name="dias"
          label="Número de Días"
          rules={rules.integer}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Form.Item
          name="aplica_porcentaje"
          label="Aplica Porcentaje"
          rules={rules.required}
          hasFeedback
        >
          <Select
            trueFalse
            onChange={(value) => {
              setAplicaPorcentaje(!value);
              if (!value) {
                form.resetFields(['porcentaje_de_percepcion']);
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={12}>
        <PercentageInput
          name="porcentaje_de_percepcion"
          label="% de Percepción"
          rules={!aplicaPorcentaje ? rules.porcentaje_de_percepcion : null}
          disabled={aplicaPorcentaje}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        <Form.Item
          name="vigente_al"
          label="Vigencia Final"
          hasFeedback
        >
          <DatePicker
            allowClear
            format="DD/MM/YYYY"
          />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={antiguedadLaboral ? columFormat1 : columFormat2}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizadoInactivo}
          disableEdit={estatusAutorizadoInactivo}
          disableDelete={estatusAutorizadoInactivo}
          permission={permission}
          mobileColIndex={0}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${antiguedadLaboral ? 'Importe Variable por Antigüedad' : 'Importe Variable por Antigüedad y Porcentaje'}`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="55%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              aplica_importe_fijo: true,
            }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="de_anos_texto"
                  label="De Número de Años"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
              {/* <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="de_anos"
                  label="De Número de Años"
                  rules={rules.integer}
                  onChange={onAnioChange}
                />
              </Col> */}
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="de_dias"
                  label="De Número de Días"
                  rules={rules.integer}
                  onChange={onDiaChange}
                />
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="hasta_anos_texto"
                  label="Hasta Número de Años"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
              </Col>
              {/* <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="hasta_anos"
                  label="Hasta Número de Años"
                  rules={rules.menor_anos}
                  onChange={onAnioChange}
                />
              </Col> */}
              <Col xs={24} sm={24} md={12}>
                <NumericInput
                  name="hasta_dias"
                  label="Hasta Número de Días"
                  rules={rules.integer}
                  onChange={onDiaChange}
                />
              </Col>
              {
                antiguedadLaboral ? renderColumnFormat1() : renderColumnFormat2()
              }
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Configuración de Antigüedad"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionConfiguracionAntiguedad;
